import axios from 'axios';
import { rpc } from './helper';
import {
  makeProxyRequest,
  makeProxyRequestWithExpectedErrors,
} from '../utils/request';
import { baseUrl, proctorExternalBaseUrl } from '../constants/AppConstants';
import { proctoringUBALogger } from '../utils/trackingUtil';
import {
  getDataInMbps,
  incrementProctorCacheBasedOnKey,
  updateInternetSpeedInfo,
} from '../utils/helperFunction';
import {
  FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY,
  PROCTOR_DROP_IMG_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_FAILED_IMG_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_INITIATED_IMAGE_UPLOAD_LOCALSTORAGE_KEY,
  PROCTOR_SIGNED_URL_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_SUCCESS_IMG_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_TABID_SESSIONSTORAGE_KEY,
  PROCTOR_TEST_START_TIME_LOCALSTORAGE_KEY,
  PROCTOR_WEBCAM_ERROR_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_URL_API_FAIL_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_URL_API_SUCCESS_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY,
} from '../constants/Proctor';
import {
  OFFLINE_DURATION_LOCALSTORAGE_KEY,
  OFFLINE_LAST_START_TIME_LOCALSTORAGE_KEY,
  OFFLINE_TIMESTAMP_LOCALSTORAGE_KEY,
} from '../constants/Constants';
import { detectBrowser } from '../utils/utils';
import { httpStatusCodes } from '../constants/Http';

export const uploadIdentityImageApi = ({
  assessmentType,
  assessmentId,
  file,
  type = '',
}) => {
  const formData = new FormData();
  const startTime = new Date();
  formData.append('file', file);
  const apiUrl = `/proctorv2/identity/create/${assessmentType}/${assessmentId}/${type}`;
  const url = `${baseUrl}${rpc(apiUrl)}`;
  const config = {
    withCredentials: true,
    header: {
      'Content-Type': 'multipart/form-data',
    },
    xsrfCookieName: 'doselectcsrf',
    xsrfHeaderName: 'X-CSRFToken',
    onUploadProgress: async progressEvent => {
      // This is temporary code to detect the upload speed
      // until we implement the feature related to consistent
      // internet connection
      if (progressEvent.loaded === progressEvent.total) {
        const endTime = new Date();
        const uploadSpeedInMbps = `${getDataInMbps({
          startTime,
          endTime,
          filesize: progressEvent.total,
        })}`;
        proctoringUBALogger(
          'info',
          `Audio Upload Speed: ${uploadSpeedInMbps} mbps`
        );

        updateInternetSpeedInfo({ uploadSpeed: uploadSpeedInMbps });
      }
    },
  };
  return axios.post(url, formData, config);
};

export const uploadAudioSampleApi = ({
  assessmentType,
  assessmentId,
  file,
  type = 'sample_audio',
}) => {
  const formData = new FormData();
  formData.append('file', file);
  const apiUrl = `/proctorv2/identity/create/${assessmentType}/${assessmentId}/${type}`;
  const url = `${baseUrl}${rpc(apiUrl)}`;
  const config = {
    withCredentials: true,
    header: {
      'Content-Type': 'multipart/form-data',
    },

    xsrfCookieName: 'doselectcsrf',
    xsrfHeaderName: 'X-CSRFToken',
  };
  return axios.post(url, formData, config);
};

export const hasIdentityImage = ({ assessmentType, assessmentId }) =>
  makeProxyRequestWithExpectedErrors({
    expectedError: httpStatusCodes.NOT_FOUND,
    method: 'get',
    url: rpc(
      `/proctorv2/identity/fetch/${assessmentType}/${assessmentId}?check_if_exists=true`
    ),
  });

export const getIdentityDetailsApi = ({
  assessmentType,
  assessmentId,
  identity = false,
  idCard = false,
  audioSample = false,
}) => {
  return makeProxyRequestWithExpectedErrors({
    expectedError: httpStatusCodes.NOT_FOUND,
    method: 'get',
    url: rpc(
      `/proctorv2/identity/fetch/v2/${assessmentType}/${assessmentId}?identity_image=${identity}&id_card_image=${idCard}&audio_sample=${audioSample}`
    ),
  });
};

export const uploadWebcamSnapshotApi = (
  signedUrl,
  compressedImageBlob,
  contentType
) => {
  const startTime = new Date();
  incrementProctorCacheBasedOnKey(
    PROCTOR_INITIATED_IMAGE_UPLOAD_LOCALSTORAGE_KEY
  );

  const axiosConfig = {
    timeout: 60000, // 60 Seconds
    headers: {
      'Content-Type': contentType,
    },
    onUploadProgress: async progressEvent => {
      // This is temporary code to detect the upload speed
      // until we implement the feature related to consistent
      // internet connection in test
      if (progressEvent.loaded === progressEvent.total) {
        const endTime = new Date();
        const uploadSpeedInMbps = `${getDataInMbps({
          startTime,
          endTime,
          filesize: progressEvent.total,
        })}`;

        updateInternetSpeedInfo({ uploadSpeed: uploadSpeedInMbps });
      }
    },
  };

  return axios.put(signedUrl, compressedImageBlob, axiosConfig);
};

// Audio proctoring: upload audio blob to signed URL
export const uploadAudioProctorSegmentApi = (
  signedUrl,
  audioBlob,
  contentType
) => {
  const headers = {
    'Content-Type': contentType,
  };
  return axios.put(signedUrl, audioBlob, { headers, timeout: 60000 });
};

export const createProctorV2Settings = ({
  assessmentType,
  assessmentId,
  isProctoringABEnabled,
}) =>
  makeProxyRequest(
    'post',
    rpc(`/proctorv2/settings/create/${assessmentType}/${assessmentId}`),
    false,
    { ab_mode: isProctoringABEnabled ? 'batch' : 'single' }
  );

export const identityImageUrl = (assessmentType, assessmentId) => {
  const url = `${baseUrl}${rpc(
    `/proctorv2/identity/fetch/${assessmentType}/${assessmentId}?check_if_exists=false`
  )}`;
  return url;
};

export const getWebcamSnapshotSignedUrl = ({
  token,
  assessmentId,
  imageSequence,
  requestTransactionId,
  mode,
}) => {
  const axiosConfig = {
    timeout: 60000, // 60 Seconds
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Transaction-Id': requestTransactionId,
    },
  };

  let networkSpeed = sessionStorage.getItem('image-internet-speed');
  networkSpeed = networkSpeed ? JSON.parse(networkSpeed) : {};
  const offlineDurationInfo = localStorage.getItem(
    OFFLINE_DURATION_LOCALSTORAGE_KEY
  );

  incrementProctorCacheBasedOnKey(PROCTOR_SIGNED_URL_COUNTER_LOCALSTORAGE_KEY);
  const payload = {
    upload_speed: networkSpeed.uploadSpeed || 0,
    download_speed: networkSpeed.downloadSpeed || 0,
    offline_duration: offlineDurationInfo || 0,
    assessment_id: assessmentId,
    failed_image_uploads:
      localStorage.getItem(PROCTOR_FAILED_IMG_COUNTER_LOCALSTORAGE_KEY) ||
      'N/A',
    proctoring_start_time: localStorage.getItem(
      PROCTOR_TEST_START_TIME_LOCALSTORAGE_KEY
    ),
    image_sequence: imageSequence === 1 ? -1 : imageSequence,
    mode,
  };
  return axios.post(
    `${proctorExternalBaseUrl}/v1/signed-url`,
    payload,
    axiosConfig
  );
};

// Audio proctoring: get signed URL for uploading audio segment
export const getAudioProctorSignedUrl = ({
  token,
  assessmentId,
  requestTransactionId,
  violationTimestamp,
  metadata = {},
  fileType = 'webm',
}) => {
  const axiosConfig = {
    timeout: 60000, // 60 Seconds
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Transaction-Id': requestTransactionId,
    },
  };

  const payload = {
    assessment_id: assessmentId,
    violation_timestamp: violationTimestamp,
    metadata,
    file_type: fileType,
  };

  return axios.post(
    `${proctorExternalBaseUrl}/v1/audio-signed-url`,
    payload,
    axiosConfig
  );
};

export const getWebcamSnapshotBatchSignedUrl = ({
  token,
  assessmentId,
  mode,
  batchSize,
  lastSignedUrl,
  nextImageSequence,
  initial_call,
}) => {
  const axiosConfig = {
    timeout: 60000, // 60 Seconds
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  let networkSpeed = sessionStorage.getItem('image-internet-speed');
  networkSpeed = networkSpeed ? JSON.parse(networkSpeed) : {};
  const offlineDurationInfo = localStorage.getItem(
    'offline-duration-in-seconds'
  );

  const payload = {
    upload_speed: networkSpeed.uploadSpeed || 0,
    download_speed: networkSpeed.downloadSpeed || 0,
    offline_duration: offlineDurationInfo || 0,
    assessment_id: assessmentId,
    batch_size: batchSize,
    mode,
    failed_image_uploads:
      localStorage.getItem(PROCTOR_FAILED_IMG_COUNTER_LOCALSTORAGE_KEY) ||
      'N/A',
    last_signed_url: lastSignedUrl,
    proctoring_start_time: localStorage.getItem(
      PROCTOR_TEST_START_TIME_LOCALSTORAGE_KEY
    ),
    image_sequence: nextImageSequence === 1 ? -1 : nextImageSequence,
    initial_call,
  };

  return axios.post(
    `${proctorExternalBaseUrl}/v1/batch-signed-url`,
    payload,
    axiosConfig
  );
};

export const proctorHeartBeatLogger = ({
  token,
  assessmentId,
  imageData,
  final = false,
  proctorSessionId,
  requestTransactionId,
}) => {
  const axiosConfig = {
    timeout: 60000, // 60 Seconds
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Transaction-Id': requestTransactionId,
    },
  };

  let networkSpeed = sessionStorage.getItem('image-internet-speed');
  networkSpeed = networkSpeed ? JSON.parse(networkSpeed) : {};
  let retryProctorCacheList = localStorage.getItem(
    FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY
  );
  retryProctorCacheList = retryProctorCacheList
    ? JSON.parse(retryProctorCacheList)
    : {};

  let lastOfflineStartTime = localStorage.getItem(
    OFFLINE_LAST_START_TIME_LOCALSTORAGE_KEY
  );
  lastOfflineStartTime = lastOfflineStartTime
    ? new Date(lastOfflineStartTime).toString()
    : 'N/A';

  const offlineDurationTimestamp = localStorage.getItem(
    OFFLINE_TIMESTAMP_LOCALSTORAGE_KEY
  );

  const payload = {
    metadata: {
      upload_speed: networkSpeed.uploadSpeed || 0,
      download_speed: networkSpeed.downloadSpeed || 0,
      proctoring_start_time: localStorage.getItem(
        PROCTOR_TEST_START_TIME_LOCALSTORAGE_KEY
      ),
      image_data: imageData,
      offline_duration:
        localStorage.getItem(OFFLINE_DURATION_LOCALSTORAGE_KEY) || 'N/A',
      fail:
        localStorage.getItem(PROCTOR_FAILED_IMG_COUNTER_LOCALSTORAGE_KEY) ||
        'N/A',
      initiated_signed_url_count:
        localStorage.getItem(PROCTOR_SIGNED_URL_COUNTER_LOCALSTORAGE_KEY) ||
        'N/A',
      initiated_image_upload:
        localStorage.getItem(PROCTOR_INITIATED_IMAGE_UPLOAD_LOCALSTORAGE_KEY) ||
        'N/A',
      drop:
        localStorage.getItem(PROCTOR_DROP_IMG_COUNTER_LOCALSTORAGE_KEY) ||
        'N/A',
      success:
        localStorage.getItem(PROCTOR_SUCCESS_IMG_COUNTER_LOCALSTORAGE_KEY) ||
        'N/A',
      final,
      signed_url_failure_count:
        localStorage.getItem(
          PROCTOR_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY
        ) || 'N/A',
      webcam_error_count:
        localStorage.getItem(PROCTOR_WEBCAM_ERROR_COUNTER_LOCALSTORAGE_KEY) ||
        'N/A',
      cached_failed_image_sequence: Object.keys(retryProctorCacheList),
      tab_id: sessionStorage.getItem(PROCTOR_TABID_SESSIONSTORAGE_KEY) || 'N/A',
      session_id: proctorSessionId,
      browser: detectBrowser(),
      last_offline_start_time: lastOfflineStartTime,
      offline_timestamp: JSON.parse(offlineDurationTimestamp),
      batched_signed_url_api_fail:
        localStorage.getItem(
          PROCTOR_BATCHED_SIGNED_URL_API_FAIL_COUNTER_LOCALSTORAGE_KEY
        ) || 'N/A',
      batched_signed_url_api_success:
        localStorage.getItem(
          PROCTOR_BATCHED_SIGNED_URL_API_SUCCESS_COUNTER_LOCALSTORAGE_KEY
        ) || 'N/A',
      batched_signed_url_unavailable:
        localStorage.getItem(
          PROCTOR_BATCHED_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY
        ) || 'N/A',
    },
  };

  // localStorage.setItem(OFFLINE_TIMESTAMP_LOCALSTORAGE_KEY, '');

  return {
    loggerPostCall: axios.post(
      `${proctorExternalBaseUrl}/v1/${assessmentId}/heartbeat`,
      payload,
      axiosConfig
    ),
    payload,
  };
};
